/* eslint-disable max-lines */
import moment from 'moment';
import momentBusinessDays from 'moment-business-days';
import cloneDeep from 'lodash/cloneDeep';

import { DataTimeline, DateBetween, Event, EventsBadge, MonthEvent } from '@/types/timeline';
import {
  EVENT_CIRCLE,
  FEB_LEAP_YEAR_RANGE_WK4,
  FEB_RANGE_WK1,
  FEB_RANGE_WK2,
  FEB_RANGE_WK3,
  FEB_RANGE_WK4,
  MONTH_WITH_30_DAYS_RANGE_WK4,
  RANGE_WK1,
  RANGE_WK2,
  RANGE_WK3,
  RANGE_WK4,
  SINGAPORE_HOLIDAYS,
} from '@/constants/time';
import { GetFieldType, JsPdf, TypeImageFromJsPdf } from '@/types/type';
import { FORMAT_DD_MM_YYYY, FORMAT_YYYY_MMM_D } from '@/constants/common';
import * as html2pdf from 'html2pdf.js';

import { PDF_FOOTER_IMAGE } from '@/assets/images/PdfFooterImage';

import { BOTTOM_BLOCK } from '@/components/ui/PDF/PdfTemplate';
import { BLOCK_PAGE_BREAK } from '@/constants/layout';

const BILLION_NUMBER = 1000000000;
const MILLION_NUMBER = 1000000;
const THOUSAND_NUMBER = 1000;

export const TIMELINE_PADDING_BOTTOM = 24;
const PADDING_DEFAULT = 24;

export const HEIGHT_OF_UNDERLINE = 9;

export const EXTEND_TIMELINE = 5;
const BATCH_SIZE = 5;

export const HTML2PDF_PAGE_BREAK = 'html2pdf__page-break';

const heightOFBottomBar = 20;
const heightOfFooter = 32;
const widthOfRightText = 150;

export const getConfigurationEnv = (key: string) => {
  return import.meta.env[key] || '';
};

export const fetchData = (url: string, token: string) => {
  return fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      return response.json();
    })
    .then((data) => {
      return data;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error('There was a problem fetching the data:', error);
    });
};

export const postFetcher = async (url: string, payload: any) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }

  return response.json();
};

export const isInRange = (value: number, from: number, to: number) => {
  return value >= from && value <= to;
};

export const formatCurrency = (
  value: number | string,
  maximumFractionDigits: number = 0,
  compact: boolean = false,
): string => {
  try {
    const numericValue = Number(value);

    if (!numericValue || isNaN(numericValue)) {
      return `$0`;
    }

    let formattedValue = numericValue;
    let suffix = '';

    if (compact) {
      if (numericValue >= BILLION_NUMBER) {
        formattedValue = numericValue / BILLION_NUMBER;
        suffix = 'B';
      } else if (numericValue >= MILLION_NUMBER) {
        formattedValue = numericValue / MILLION_NUMBER;
        suffix = 'M';
      } else if (numericValue >= THOUSAND_NUMBER) {
        formattedValue = numericValue / THOUSAND_NUMBER;
        suffix = 'K';
      }
    }

    const formattedCurrency = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits,
    }).format(formattedValue);

    return `${formattedCurrency}${suffix}`;
  } catch {
    return `$0`;
  }
};

export function lodashGet<TData, TPath extends string, TDefault = GetFieldType<TData, TPath>>(
  data: TData,
  path: TPath,
  defaultValue?: TDefault,
): GetFieldType<TData, TPath> | TDefault {
  const value = path
    .split(/[.[\]]/)
    .filter(Boolean)
    .reduce<GetFieldType<TData, TPath>>(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (value, key) => (value as any)?.[key],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data as any,
    );

  return value !== undefined ? value : (defaultValue as TDefault);
}

export const calculatePercentagePassed = (day: number, month: number, year: number) => {
  if (day < 1 || day > 31) {
    return {
      day,
      percentage: 0,
    };
  }

  const dayRanges = [
    getDateRangeByMonth('wk1', month, year),
    getDateRangeByMonth('wk2', month, year),
    getDateRangeByMonth('wk3', month, year),
    getDateRangeByMonth('wk4', month, year),
  ];

  let percentage = 0;

  for (const range of dayRanges) {
    const [start, end] = range;

    if (day >= start && day <= end) {
      percentage = (day - start + 1) / (end - start + 1);
      break;
    }
  }

  return {
    percentage,
    day,
  };
};

const createEventsByWeek = (week: string, month: number, year: number, dataMonth: MonthEvent) => {
  const events = lodashGet(dataMonth, week) as Event;

  return {
    name: week,
    isEvent: !!lodashGet(dataMonth, week),
    progress:
      events &&
      events.map((item) => {
        return {
          ...calculatePercentagePassed(item.date, month, year),
          isSpecialEvent: item.isSpecialEvent,
          isRangeTimeline: item.isRangeTimeline,
          date: item.date,
          isHidden: item?.isHidden,
        };
      }),
  };
};

export const getWeekRanges = (
  day: number,
  month: number,
  year: number,
  dataMonth: MonthEvent,
  isMobile?: boolean,
) => {
  const week1Range = getDateRangeByMonth('wk1', month, year);
  const week2Range = getDateRangeByMonth('wk2', month, year);
  const week3Range = getDateRangeByMonth('wk3', month, year);

  const eventWk1 = createEventsByWeek('wk1', month, year, dataMonth);
  const eventWk2 = createEventsByWeek('wk2', month, year, dataMonth);
  const eventWk3 = createEventsByWeek('wk3', month, year, dataMonth);
  const eventWk4 = createEventsByWeek('wk4', month, year, dataMonth);

  if (!isMobile) {
    return [eventWk1, eventWk2, eventWk3, eventWk4];
  }

  if (day >= week1Range[0] && day <= week1Range[1]) {
    return [eventWk1, eventWk2, eventWk3, eventWk4];
  }

  if (day >= week2Range[0] && day <= week2Range[1]) {
    return [eventWk2, eventWk3, eventWk4];
  }

  if (day >= week3Range[0] && day <= week3Range[1]) {
    return [eventWk3, eventWk4];
  }

  return [eventWk4];
};

export const getWeekbyDate = (day: number, month: number, year: number) => {
  const week1Range = getDateRangeByMonth('wk1', month, year);
  const week2Range = getDateRangeByMonth('wk2', month, year);
  const week3Range = getDateRangeByMonth('wk3', month, year);

  if (day >= week1Range[0] && day <= week1Range[1]) {
    return 'wk1';
  }

  if (day >= week2Range[0] && day <= week2Range[1]) {
    return 'wk2';
  }

  if (day >= week3Range[0] && day <= week3Range[1]) {
    return 'wk3';
  }

  return 'wk4';
};

export const numberToMonthName = (monthNumber: number) => {
  return moment().month(monthNumber).format('MMM');
};

export const isMonthWith30Days = (month: number) => {
  const lastDayOfMonth = moment({ month }).endOf('month');

  // Check if the last day is the 30th day of the month
  return lastDayOfMonth.date() === 30;
};

export const getDateRangeByMonth = (week: string, month: number, year: number) => {
  switch (week) {
    case 'wk1': {
      return numberToMonthName(month) === 'Feb' ? FEB_RANGE_WK1 : RANGE_WK1;
    }

    case 'wk2': {
      return numberToMonthName(month) === 'Feb' ? FEB_RANGE_WK2 : RANGE_WK2;
    }

    case 'wk3': {
      return numberToMonthName(month) === 'Feb' ? FEB_RANGE_WK3 : RANGE_WK3;
    }

    case 'wk4': {
      if (numberToMonthName(month) === 'Feb') {
        return moment({ month, year }).endOf('month').date() === 28
          ? FEB_RANGE_WK4
          : FEB_LEAP_YEAR_RANGE_WK4;
      }

      return moment({ month }).endOf('month').date() === 30
        ? MONTH_WITH_30_DAYS_RANGE_WK4
        : RANGE_WK4;
    }

    default: {
      return [];
    }
  }
};

export const getMonthsBetweenDates = (
  startDate: moment.Moment,
  endDate: moment.Moment,
  dataTimeline: DataTimeline,
  isMobile?: boolean,
) => {
  const currentDate = moment(startDate);

  const endDateMoment = moment(endDate);

  const monthNumber = endDateMoment.diff(currentDate, 'months') || 1;

  const dataMonthEventCurrentDate = lodashGet(
    dataTimeline,
    `${currentDate.year()}.${numberToMonthName(currentDate.month())}`,
  ) as MonthEvent;

  const months = [
    {
      year: currentDate.year(),
      month: numberToMonthName(currentDate.month()),
      weeks: getWeekRanges(
        currentDate.date(),
        currentDate.month(),
        currentDate.year(),
        dataMonthEventCurrentDate,
        isMobile,
      ),
    },
  ];

  [...Array(monthNumber || 0)].forEach((_, index) => {
    const newDate = moment(currentDate.toDate()).add(index + 1, 'months');

    const dataMonth = lodashGet(
      dataTimeline,
      `${newDate.year()}.${numberToMonthName(newDate.month())}`,
    ) as MonthEvent;

    months.push({
      year: newDate.year(),
      month: numberToMonthName(newDate.month()),
      weeks: [
        {
          ...createEventsByWeek('wk1', newDate.month(), newDate.year(), dataMonth),
        },
        {
          ...createEventsByWeek('wk2', newDate.month(), newDate.year(), dataMonth),
        },
        {
          ...createEventsByWeek('wk3', newDate.month(), newDate.year(), dataMonth),
        },
        {
          ...createEventsByWeek('wk4', newDate.month(), newDate.year(), dataMonth),
        },
      ],
    });
  });

  const monthsWithEventWeeks = months.map((month) => {
    return month.weeks.some((w) => w.isEvent) ? month : { ...month, weeks: null };
  });

  return monthsWithEventWeeks;
};

export const compareTwoDay = (dateString1: string, dateString2: string) => {
  const date1 = moment(dateString1, 'YYYY-MMM-DD');
  const date2 = moment(dateString2, 'YYYY-MMM-DD');

  const distance = date2.diff(date1, 'days');

  return distance;
};

export const calculateTimelineSpecialEventHeight = (numberOfEvents: number) => {
  return new Promise(async (resolve) => {
    const elements = document.querySelectorAll('[data-event="event"]');

    if (elements.length === 0) {
      return 0;
    }

    const firstEventPosition = elements[0].getBoundingClientRect().top;
    const lastEventPosition = elements[numberOfEvents - 1].getBoundingClientRect().bottom;

    const timelineDistance = lastEventPosition - firstEventPosition;
    const distance = timelineDistance - EVENT_CIRCLE;

    const eventsTimeLine = document.getElementById('event-special-timeline')?.style;

    if (eventsTimeLine) {
      const transformEventTimeline =
        firstEventPosition -
        (document.getElementById('timeline')?.getBoundingClientRect().top || 0);

      eventsTimeLine.height = `${distance}px`;
      eventsTimeLine.transform = `translateY(${transformEventTimeline + EVENT_CIRCLE / 2}px)`;
    }

    return resolve(distance);
  });
};

// for mobile
export const calculateTimelineEventBadge = (
  eventsBadge: EventsBadge,
  callback: (data: EventsBadge) => void,
) => {
  return new Promise(async (resolve) => {
    let maxHeight = 0;
    let lastPositionOfBadge = 0;
    let cloneEventsBadge: EventsBadge = [cloneDeep(eventsBadge[0])];
    const badges = cloneDeep(eventsBadge);

    badges.forEach((item, index) => {
      const event =
        document
          .getElementById(moment(item.date).format(FORMAT_YYYY_MMM_D))
          ?.getBoundingClientRect().top || 0;

      const positionTimeline =
        document.getElementById('event-badge')?.getBoundingClientRect().top || 0;

      const eventLabel = document.getElementById(`event-badge-${item.date}`);

      if (eventLabel) {
        const positionOfBadge = event - positionTimeline - 1;

        eventLabel.style.transform = `translateY(${positionOfBadge}px)`;

        const isOverlap = index !== 0 && lastPositionOfBadge > positionOfBadge;

        // if overlapping merge and add underline
        if (isOverlap) {
          const allExist = item.events.every((item) =>
            cloneEventsBadge[cloneEventsBadge.length - 1].events.includes(item),
          );

          //Only add the event if it's not added before
          if (!allExist) {
            cloneEventsBadge[cloneEventsBadge.length - 1].events = [
              ...cloneEventsBadge[cloneEventsBadge.length - 1].events,
              { text: '', isUnderline: true },
              ...item.events,
            ];
          }

          //after merge re update position = height of badge + old position + height of underline - total padding (top and bottom)
          lastPositionOfBadge =
            positionOfBadge + eventLabel?.clientHeight + HEIGHT_OF_UNDERLINE - 16;
        }

        // if not overlapping keep old position
        if (!isOverlap) {
          lastPositionOfBadge = positionOfBadge + eventLabel?.clientHeight;
        }

        // if not overlapping add new badge
        if (!isOverlap && index !== 0) {
          cloneEventsBadge = [...cloneEventsBadge, item];
        }
      }

      maxHeight =
        (eventLabel?.clientHeight || 0) > maxHeight
          ? (eventLabel?.clientHeight || 0) + event - positionTimeline - 1
          : maxHeight;
    });

    if (cloneEventsBadge.length !== eventsBadge.length) {
      callback(cloneEventsBadge);
    }

    const styleTimelineContainer = document.getElementById('timeline-container');

    if (styleTimelineContainer) {
      styleTimelineContainer.style.height = `${
        (maxHeight > styleTimelineContainer.scrollHeight
          ? maxHeight
          : styleTimelineContainer.scrollHeight) + TIMELINE_PADDING_BOTTOM
      }px`;
    }

    resolve(true);
  });
};

const getWorkingDayBefore = (date: moment.Moment): moment.Moment => {
  const previousDay = date.clone().subtract(1, 'day');

  if (previousDay.day() === 0) {
    return previousDay.clone().subtract(2, 'days');
  }

  if (previousDay.day() === 6) {
    return previousDay.clone().subtract(1, 'days');
  }

  return previousDay;
};

export const getDateBeforeWeekend = (inputDate: moment.Moment, addDays?: number) => {
  let futureDate = inputDate.clone();

  if (addDays) {
    futureDate = futureDate.add(addDays, 'days');
  }

  // Check if the future date falls on a weekend
  if (futureDate.day() === 0 || futureDate.day() === 6) {
    const workingDayBefore = getWorkingDayBefore(futureDate);

    return workingDayBefore;
  }

  return futureDate;
};

export const getDateAfterWeekend = (date: moment.Moment) => {
  const inputDate = date.clone();

  // Check if the given date falls on a weekend (Saturday or Sunday)
  if (inputDate.day() === 0 /* Sunday */ || inputDate.day() === 6 /* Saturday */) {
    // If it's a weekend, add the necessary number of days to get to the next weekday (Monday)
    const daysToAdd = inputDate.day() === 0 ? 1 : 2;

    inputDate.add(daysToAdd, 'days');
  }

  return inputDate;
};

export const getWorkingDateAfterHolidayAndWeekend = (date: moment.Moment): moment.Moment => {
  // Create a moment-business-days instance
  const businessDate = momentBusinessDays(date);

  // If the current date is not a business day, get the next business day
  if (!businessDate.isBusinessDay()) {
    return businessDate.nextBusinessDay();
  }

  return businessDate;
};

export const addWorkingDays = (startDate: moment.Moment, workingDays: number): moment.Moment => {
  // Create a moment-business-days instance from the start date
  const businessDate = momentBusinessDays(startDate);

  // Add the specified number of working days
  return businessDate.businessAdd(workingDays);
};

export const roundNumberNearHalf = (rating: number) => {
  return (Math.round(rating * 2) / 2).toFixed(1);
};

export const roundNumberTwoDecimal = (number: number) => {
  return Math.ceil(number * 100) / 100;
};

export const arrayToObject = <T>(arr: T[], key: keyof T): { [k: string]: T } =>
  arr.reduce(
    (obj, item) => {
      const keyValue = item[key];

      if (keyValue !== undefined) {
        obj[String(keyValue)] = item;
      }

      return obj;
    },
    {} as { [k: string]: T },
  );

export const capitalizeFirstLetter = (string: string) => {
  if (typeof string === 'string') {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  return '';
};

export const capitalizeFirstLetterOfEachWord = (str: string | null | undefined): string => {
  if (!str) {
    return '';
  }

  return str.replace(/\b\w/g, (match) => {
    return match.toUpperCase();
  });
};

export const isWebview = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const navigator = window.navigator;

  const standalone = (navigator as any).standalone;
  const userAgent = navigator.userAgent.toLowerCase();
  const safari = /safari/.test(userAgent);
  const ios = /iphone|ipod|ipad/.test(userAgent);

  return ios ? !standalone && !safari : /\bwv\b/.test(userAgent);
};

const prepareImages = async (element: HTMLElement) => {
  const bottomElement = element
    .getElementsByClassName(BOTTOM_BLOCK)[0]
    ?.cloneNode(true) as HTMLElement;
  const headerElement = element.getElementsByClassName('pdf-header')[0];

  const headerImage = (await html2pdf
    .default()
    .set({ html2canvas: { useCORS: true, scale: 2 } })
    .from(headerElement)
    .toImg()
    .outputImg()) as TypeImageFromJsPdf;

  const imageBottom = (await html2pdf
    .default()
    .set({ html2canvas: { useCORS: true, scale: 2 } })
    .from(bottomElement)
    .toImg()
    .outputImg()) as TypeImageFromJsPdf;

  return [headerImage, imageBottom];
};

const getPdfOptions = (fileName: string) => ({
  margin: [85, 0, 40, 0],
  filename: fileName,
  autoPaging: 'text',
  pagebreak: {
    mode: ['css'],
    after: [`.${HTML2PDF_PAGE_BREAK}`],
    avoid: ['.disclaimer-block', '.qr-block', '.pdf-chart', '.avoid-cutting'],
    before: ['.page-break-before'],
  },

  image: { type: 'jpeg', quality: 2 },
  html2canvas: {
    dpi: 192,
    useCORS: true,
    scale: 1.5,
    y: 0,
    x: 0,
    scrollY: 0,
    scrollX: 0,
    letterRendering: true,
    allowTaint: true,
  },
  jsPDF: { unit: 'px', orientation: 'p', format: [794, 1123], compress: true },
});

const setupCloneDocument = () => {
  const cloneDocument = document.cloneNode(true) as Document;
  const viewport = cloneDocument.querySelector('meta[name=viewport]');

  viewport?.setAttribute(
    'content',
    'width=1024, initial-scale=1, maximum-scale=1, user-scalable=no',
  );

  return cloneDocument;
};

const addHeaderAndFooterPdf = async (
  pdf: JsPdf,
  headerImage: TypeImageFromJsPdf,
  imageBottom: TypeImageFromJsPdf,
  user?: { fullName?: string; regNumber?: string; mobile?: string | number },
) => {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  await pdf.setFont('Helvetica');
  pdf.setFontType('bold');

  const totalPages = pdf.internal.getNumberOfPages();

  for (let index = 1; index <= totalPages; index++) {
    pdf.setPage(index);
    pdf.addImage(
      headerImage.src,
      'JPG',
      24,
      0,
      pdfWidth - PADDING_DEFAULT * 2,
      headerImage.height / 2,
      'header-block',
    );

    pdf.setFontSize(12);
    pdf.setTextColor('#6B6B6B');

    user &&
      pdf.text(
        `${String(index)} of ${String(totalPages)}`,
        PADDING_DEFAULT,
        pdfHeight - heightOfFooter,
      );

    pdf.text(
      `Generated on ${moment().format('DD/MM/YY, HH:mm')}`,
      pdfWidth - widthOfRightText,
      pdfHeight - heightOfFooter,
    );

    pdf.addImage(
      PDF_FOOTER_IMAGE,
      'JPG',
      0,
      pdfHeight - heightOFBottomBar,
      pdfWidth,
      heightOFBottomBar,
      'footer',
    );

    if (index === totalPages && imageBottom.src && imageBottom.src !== 'data:,') {
      pdf.addImage(
        imageBottom.src,
        'JPG',
        24,
        pdfHeight - imageBottom.height / 2 - heightOFBottomBar - PADDING_DEFAULT * 2,
        pdfWidth - PADDING_DEFAULT * 2,
        imageBottom.height / 2,
        BOTTOM_BLOCK,
      );
    }
  }
};

const workerAction = async ({
  worker,
  element,
  callBackWorker,
}: {
  worker: JsPdf;
  element: Element;
  callBackWorker: (pdf: JsPdf) => void;
}) => {
  await worker
    .from(element)
    .toContainer()
    .toCanvas()
    .toPdf()
    .get('pdf')
    .then(async (pdf: JsPdf) => {
      callBackWorker(pdf);
    });
};

export const getHtml2PDF = async (
  input: string,
  name: string,
  user?: { fullName?: string; regNumber?: string; mobile?: string | number },
) => {
  const fileName = getPdfFileName(name);
  const opt = getPdfOptions(fileName);

  //Set device always export with tablet size
  const cloneDocument = setupCloneDocument();

  const style = document.createElement('style');

  //Text align center in PDF
  document.head.appendChild(style);
  style.sheet?.insertRule('body > div:last-child img { display: inline-block; }');

  const element = cloneDocument.getElementById(input) as HTMLElement;

  element?.classList.add('pdf-format');
  const bottomElement = element
    .getElementsByClassName(BOTTOM_BLOCK)[0]
    ?.cloneNode(true) as HTMLElement;

  bottomElement?.classList.add('pdf-format');

  const [headerImage, imageBottom] = await prepareImages(element);

  const sections = element.getElementsByClassName(BLOCK_PAGE_BREAK);

  const worker = html2pdf.default().set(opt);

  // If less than 10 pages, keep the old logic
  // This section of code is already implemented and does not need to be modified
  if (sections.length < BATCH_SIZE) {
    [...sections].map((item, indexItem, array) => {
      if (indexItem < array.length - 1) {
        item?.classList.add(HTML2PDF_PAGE_BREAK);
      }

      item?.classList.remove('hidden');

      return item;
    });

    await workerAction({
      worker,
      element,
      async callBackWorker(pdf) {
        await addHeaderAndFooterPdf(pdf, headerImage, imageBottom, user);
      },
    });

    await savePdf(worker, fileName);

    return worker;
  }

  // This function processes the sections in batches
  const processInBatches = async () => {
    // Hide all sections
    [...sections].map((item) => {
      item?.classList.add('hidden');

      item?.classList.remove(HTML2PDF_PAGE_BREAK);

      return item;
    });

    // Loop through the sections in batches
    for (let i = 0, index = 1; i < sections.length; i += BATCH_SIZE, index++) {
      // Check if this is the last batch
      const isLatestRun = i + BATCH_SIZE > sections.length;

      // If not the last batch, remove the page break from the last section of the previous batch
      if (!isLatestRun) {
        const lastPdfBlock = index * BATCH_SIZE - 1;

        if (sections[lastPdfBlock]) {
          sections?.[lastPdfBlock]?.classList?.remove(HTML2PDF_PAGE_BREAK);
        }
      }

      // If this is not the first batch, hide the hidden name
      if (index > 1) {
        const hiddenName = element?.querySelector('.hidden-name');

        if (hiddenName) {
          hiddenName.classList.add('hidden');
        }
      }

      // Get the sections for this batch
      const itemQueue = [...sections].slice(
        (index - 1) * BATCH_SIZE,
        (index - 1) * BATCH_SIZE + BATCH_SIZE,
      );

      // Loop through the sections in this batch
      itemQueue.map((item, indexItem, array) => {
        // Add a page break to all sections except the last one
        if (indexItem < array.length - 1) {
          item.classList.add(HTML2PDF_PAGE_BREAK);
        }

        // Show the section
        item.classList.remove('hidden');

        return item;
      });

      // Process the batch
      await workerAction({
        worker,
        element,
        async callBackWorker(pdf) {
          // If this is the last batch, add the content to the PDF
          if (isLatestRun) {
            await addHeaderAndFooterPdf(pdf, headerImage, imageBottom, user);
          }

          // Hide all sections in the batch
          itemQueue.map((item) => {
            item.classList.remove(HTML2PDF_PAGE_BREAK);
            item.classList.add('hidden');

            return item;
          });

          // If this is not the last batch, add a new page to the PDF
          if (i + BATCH_SIZE < sections.length) {
            pdf.addPage();
          }
        },
      });
    }

    style.remove();

    await savePdf(worker, fileName);

    return worker;
  };

  await processInBatches();
};

const savePdf = async (worker: ReturnType<typeof html2pdf.default>, fileName: string) => {
  if (!isWebview()) {
    await worker.save(fileName);

    return worker;
  }

  try {
    const userAgent = navigator.userAgent.toLowerCase();
    const pdfRaw = await worker?.output('datauristring');

    const pdfBase64 = pdfRaw?.replace(
      'data:application/pdf;',
      `data:application/pdf;filename=${fileName}.pdf;`,
    );

    if (/iphone|ipod|ipad/i.test(userAgent)) {
      (window as any)?.webkit?.messageHandlers.downloadPdf.postMessage(pdfBase64);

      return worker;
    }

    if (typeof Android !== 'undefined' && Android && /android/i.test(userAgent)) {
      Android?.downloadPdf(pdfBase64);

      return worker;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving PDF:', error);
  }
};

export const getPdfFileName = (calculatorName: string) => {
  const fileName = calculatorName
    ?.toUpperCase()
    .replace(/ /g, '_')
    .concat('_')
    .concat(moment(new Date()).format(FORMAT_DD_MM_YYYY));

  return fileName;
};

export const getNumberWithPercent = (number: number) => {
  return number * 100;
};

export const getNumberWithoutPercent = (number: number) => {
  return number / 100;
};

export const fillDiv = (elWidth: number, elHeight: number) => {
  const outside = document.getElementById('scalable-wrapper');

  if (outside) {
    const scale = Math.min(outside.clientWidth / elWidth, outside.clientHeight / elHeight);

    if (scale < 1) {
      outside.style.height = `${outside.clientHeight * scale}px`;
    }

    return scale;
  }

  return 1;
};

export const getDecimalValue = (number: number) => {
  if (number % 1 === 0) {
    return number;
  }

  return number.toFixed(2);
};

export function reformatPhoneNumber(phoneNumber: string, countryCode: string): string {
  const cleanNumber = phoneNumber.replace(/\D/g, '');

  const formattedNumber = `${countryCode ? '+' + countryCode + ' ' : ''}${cleanNumber.slice(0, 4)} ${cleanNumber.slice(4)}`;

  return formattedNumber;
}

export const sumByKey = (array: { [key: string]: number | string }[], key: string) => {
  return array.reduce((a, b) => a + (+b[key] || 0), 0);
};

export const getLetters = (index: number) => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  return letters[index];
};

export const policyField = ([from, to]: number[], floatValue?: number) => {
  const num = floatValue || 0;

  return !floatValue || isInRange(num, from, to);
};

export const calculateHorizontalTimelineBetweenEvent = (
  dateBetween: DateBetween,
  position?: string,
) => {
  dateBetween.forEach((label) => {
    const event1Id = position ? `${position}-${label.event1}` : label.event1;
    const event2Id = position ? `${position}-${label.event2}` : label.event2;
    const eventLabelId = position ? `${position}-event-label` : 'event-label';
    const event1LabelId = position ? `${position}-event-${label.event1}` : `event-${label.event1}`;

    const event1 = document.getElementById(event1Id)?.getBoundingClientRect().right || 1;
    const event2 = document.getElementById(event2Id)?.getBoundingClientRect().left || 0;

    const positionTimeline =
      document.getElementById(eventLabelId)?.getBoundingClientRect().left || 0;

    const distance = event2 - event1;
    const eventLabel = document.getElementById(event1LabelId)?.style;

    if (eventLabel) {
      eventLabel.transform = `translateX(${event1 - positionTimeline}px)`;
      eventLabel.width = `${distance}px`;
    }
  });
};

export const calculateVerticalTimelineBetweenEvent = (
  dateBetween: DateBetween,
  position?: string,
) => {
  return new Promise(async (resolve) => {
    dateBetween.forEach((label) => {
      const event1Id = position ? `${position}-${label.event1}` : label.event1;
      const event2Id = position ? `${position}-${label.event2}` : label.event2;
      const eventLabelId = position ? `${position}-event-label` : 'event-timeline';
      const event1LabelId = position
        ? `${position}-event-${label.event1}`
        : `event-${label.event1}`;

      const event1 = document.getElementById(event1Id)?.getBoundingClientRect().bottom || 1;
      const event2 = document.getElementById(event2Id)?.getBoundingClientRect().top || 0;

      const positionTimeline =
        document.getElementById(eventLabelId)?.getBoundingClientRect().top || 0;

      const distance = event2 - event1;
      const eventLabel = document.getElementById(event1LabelId)?.style;

      if (eventLabel) {
        eventLabel.transform = `translateY(${event1 - positionTimeline}px)`;
        eventLabel.height = `${distance}px`;
      }
    });

    resolve(true);
  });
};

export const calculateTimelineEventHeight = (position?: string, index?: number) => {
  return new Promise(async (resolve) => {
    const dataEvent = position ? `[data-event="event-${position}"]` : '[data-event="event"]';
    const eventTimeLineId = position ? `${position}-event-timeline` : 'event-timeline';
    const timelineId = index !== undefined ? `timeline-${index}` : 'timeline';

    const elements = document.querySelectorAll(dataEvent);

    if (elements.length === 0) {
      return 0;
    }

    const fistEventPosition = elements[0].getBoundingClientRect().top;
    const lastEventPosition = elements[elements.length - 1].getBoundingClientRect().bottom;

    const distance = lastEventPosition - fistEventPosition + EVENT_CIRCLE / 2;

    const eventsTimeLine = document.getElementById(eventTimeLineId)?.style;

    if (eventsTimeLine) {
      const transformEventTimeline =
        fistEventPosition - (document.getElementById(timelineId)?.getBoundingClientRect().top || 0);

      eventsTimeLine.height = `${distance}px`;
      eventsTimeLine.transform = `translateY(${transformEventTimeline - 2}px)`;
    }

    return resolve(distance);
  });
};

export const calculateTimelineEventWidth = (position?: string) => {
  const eventTimeLineId = position ? `${position}-event-timeline` : 'event-timeline';

  const elements = document.querySelectorAll('[data-event="event"]');

  if (elements.length === 0) {
    return 0;
  }

  const firstEventPosition = elements[0].getBoundingClientRect().left;
  const lastEventPosition = elements[elements.length - 1].getBoundingClientRect().right;

  const distance = lastEventPosition - firstEventPosition + EVENT_CIRCLE / 2;

  const eventsTimeLine = document.getElementById(eventTimeLineId)?.style;

  if (eventsTimeLine) {
    const transformEventTimeline =
      firstEventPosition - (document.getElementById('timeline')?.getBoundingClientRect().left || 0);

    eventsTimeLine.width = `${distance}px`;
    eventsTimeLine.transform = `translateX(${transformEventTimeline - 2}px)`;
  }

  return distance;
};

export const getOrdinal = (n: number) => {
  let ord = 'th';

  if (n % 10 === 1 && n % 100 !== 11) {
    ord = 'st';
  } else if (n % 10 === 2 && n % 100 !== 12) {
    ord = 'nd';
  } else if (n % 10 === 3 && n % 100 !== 13) {
    ord = 'rd';
  }

  return ord;
};

/**
 * Disables console warnings in production environment
 * This function removes console methods that typically show warnings
 */
export const disableConsoleWarningsInProduction = () => {
  if (process.env.NODE_ENV === 'production') {
    // Disable console.warn
    // eslint-disable-next-line no-console
    console.warn = () => {};

    // eslint-disable-next-line no-console
    console.error = () => {};

    // eslint-disable-next-line no-console
    console.log = () => {};
  }
};

export const configureMomentLocale = () => {
  momentBusinessDays.updateLocale('en', {
    workingWeekdays: [1, 2, 3, 4, 5], // Mon-Fri
    holidays: SINGAPORE_HOLIDAYS.map((holiday) => momentBusinessDays(holiday).format('YYYY-MM-DD')),
    holidayFormat: 'YYYY-MM-DD',
  });
};
